import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CustomInput from "./CustomInput";
import CustomButton from "./CustomButton";
import {
  applyCashInMoneyOperation,
  applyCashOutMoneyOperation,
  applyInternalTransferMoneyOperation,
  applyExchangeRateOperation,
  applyDepositWithdrawOperation,
  applyPayByCardOperation,
  applyCollectCashInRequest,
  applyCollectCashOutRequest,
  applyTransferCashOutRequest,
  cancelDepositDemoOperation,
  cancelDepositWithdrawOperation,
  cancelCashInMoneyOperation,
  cancelCashOutMoneyOperation,
  cancelInternalTransferMoneyOperation,
  cancelExchangeRateOperation,
  cancelPayByCardOperation,
  verifyUserPassword,
} from "../utils/apiClient";
import { applyDepositDemoOperation } from "../controller/accountsDemo/createDepositDemoController";
import AppConstants from "../utils/appConstants";

/**
 * Shared password dialog for cash-in / cash-out (mirrors Flutter DialogWidget).
 * On Apply: calls apply API with password, then onClose(result).
 * On Cancel (button or overlay): calls cancel API, then onClose('canceled').
 *
 * @param {boolean} open
 * @param {(result: 'ok' | 'need_admin' | 'canceled' | 'failed' | 'invalid_password', data?: { transactionId?: string, url?: string, responseData?: object }) => void} onClose
 * @param {'cashin' | 'cashout' | 'internal_transfer' | 'demo_deposit' | 'exchange' | 'ib_transfer' | 'pay_by_card' | 'collect_cash_in' | 'collect_cash_out' | 'transfer_cash_out' | 'verify'} mode
 * @param {string} requestToken - REQUEST_TOKEN from create response
 * @param {string} firstMessage - e.g. "You will withdraw X, you will receive Y after commission"
 * @param {string} [secondMessage] - optional secondary message
 * @param {string} [title] - dialog title
 * @param {boolean} [openExternalPaymentWindow] - open a tab before the async apply so popup blockers allow Whish
 */
export default function TransactionPasswordDialog({
  open,
  onClose,
  mode,
  requestToken,
  firstMessage = "",
  secondMessage = "",
  title,
  openExternalPaymentWindow = false,
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setPassword("");
      setShowPassword(false);
      setError(null);
      setLoading(false);
    }
  }, [open]);

  if (!open) return null;

  const isVerifyMode = mode === "verify";
  const dialogTitle =
    title ||
    (isVerifyMode ? "Confirm download" : "Confirm transaction");

  const handleApply = async () => {
    const p = (password || "").trim();
    if (!p) {
      setError("Please enter your password.");
      return;
    }
    setError(null);
    setLoading(true);
    let paymentWindow = null;
    if (openExternalPaymentWindow && typeof window !== "undefined") {
      try {
        paymentWindow = window.open("about:blank", "mfg-whish-payment");
      } catch (_) {
        paymentWindow = null;
      }
    }
    const closePaymentWindow = () => {
      if (!paymentWindow || paymentWindow.closed) return;
      try {
        paymentWindow.close();
      } catch (_) {}
    };
    try {
      if (isVerifyMode) {
        const res = await verifyUserPassword(p);
        const d = res.decryptedData || res.data || {};
        const code = Number(d.CODE ?? d.code ?? 0);
        if (code === AppConstants.responseOk || code === 200) {
          onClose("ok", { responseData: d });
          return;
        }
        if (code === AppConstants.responseNeedPermission || code === 204 || code === AppConstants.responseError || code === 208) {
          setError("Invalid password. Please try again.");
          setLoading(false);
          return;
        }
        setError(d.MESSAGE || d.message || "Could not verify password.");
        setLoading(false);
        return;
      }
      const applyFn =
        mode === "cashout"
          ? applyCashOutMoneyOperation
          : mode === "internal_transfer"
          ? applyInternalTransferMoneyOperation
          : mode === "demo_deposit"
          ? applyDepositDemoOperation
          : mode === "exchange"
          ? applyExchangeRateOperation
          : mode === "ib_transfer"
          ? applyDepositWithdrawOperation
          : mode === "pay_by_card"
          ? applyPayByCardOperation
          : mode === "collect_cash_in"
          ? applyCollectCashInRequest
          : mode === "collect_cash_out"
          ? applyCollectCashOutRequest
          : mode === "transfer_cash_out"
          ? applyTransferCashOutRequest
          : applyCashInMoneyOperation;
      const res =
        mode === "cashin"
          ? await applyCashInMoneyOperation(requestToken, p, {
              returnBaseUrl:
                typeof window !== "undefined" ? window.location.origin : undefined,
            })
          : await applyFn(requestToken, p);
      const d = res.decryptedData || res.data || {};
      if (mode === "internal_transfer") {
        console.log("[InternalTransfer][Apply] response:", {
          requestToken,
          rawResponse: res,
          parsedData: d,
        });
      }
      const code = Number(d.CODE ?? d.code ?? 0);

      if (
        mode === "pay_by_card" &&
        code === AppConstants.responseError
      ) {
        closePaymentWindow();
        setError("Invalid password. Please try again.");
        setLoading(false);
        return;
      }

      const isBrokerCollectMode =
        mode === "collect_cash_in" ||
        mode === "collect_cash_out" ||
        mode === "transfer_cash_out";

      if (
        isBrokerCollectMode &&
        (code === AppConstants.responseFailed || code === 207)
      ) {
        closePaymentWindow();
        const msg = d.MESSAGE || d.message || "Operation failed.";
        setError(msg);
        setLoading(false);
        return;
      }

      if (
        code === AppConstants.responseOk ||
        code === 200 ||
        (mode === "pay_by_card" &&
          (code === AppConstants.responseAlreadyOk || code === 205))
      ) {
        const url = d.URL != null ? String(d.URL).trim() : null;
        const payload = url ? { url, responseData: d } : { responseData: d };
        if (requestToken) {
          payload.transactionId = requestToken;
        }
        if (url && paymentWindow && !paymentWindow.closed) {
          try {
            paymentWindow.location.href = url;
            payload.paymentWindow = paymentWindow;
          } catch (_) {
            closePaymentWindow();
          }
        } else if (!url) {
          closePaymentWindow();
        }
        onClose("ok", payload);
        return;
      }
      if (code === AppConstants.responseNeedAdminApproval || code === 201) {
        closePaymentWindow();
        onClose("need_admin", { responseData: d });
        return;
      }
      if (code === AppConstants.responseNeedPermission || code === 204) {
        closePaymentWindow();
        setError("Invalid password. Please try again.");
        setLoading(false);
        return;
      }
      if (isBrokerCollectMode && (code === AppConstants.responseAlreadyOk || code === 205)) {
        closePaymentWindow();
        onClose("ok", { responseData: d });
        return;
      }
      closePaymentWindow();
      const errorMessage = d.MESSAGE || d.message || "Transaction failed.";
      setError(errorMessage);
      onClose("failed", { responseData: d });
    } catch (err) {
      closePaymentWindow();
      if (mode === "internal_transfer") {
        console.error("[InternalTransfer][Apply] error:", {
          requestToken,
          error: err,
          responseData: err?.response?.data,
        });
      }
      console.error("Transaction password dialog error:", err);
      const errorMessage = err.response?.data?.MESSAGE || 
                          err.response?.data?.message || 
                          err.message || 
                          "Request failed.";
      setError(errorMessage);
      onClose("failed", { error: err });
    }
  };

  const handleCancel = async () => {
    if (loading) return;
    setLoading(true);
    setError(null);

    if (
      mode === "collect_cash_in" ||
      mode === "collect_cash_out" ||
      mode === "transfer_cash_out" ||
      mode === "verify"
    ) {
      setLoading(false);
      onClose("canceled");
      return;
    }

    try {
      const cancelFn =
        mode === "cashout"
          ? cancelCashOutMoneyOperation
          : mode === "internal_transfer"
          ? cancelInternalTransferMoneyOperation
          : mode === "demo_deposit"
          ? cancelDepositDemoOperation
          : mode === "ib_transfer"
          ? cancelDepositWithdrawOperation
          : mode === "exchange"
          ? cancelExchangeRateOperation
          : mode === "pay_by_card"
          ? cancelPayByCardOperation
          : cancelCashInMoneyOperation;
      await cancelFn(requestToken);
    } catch (_) {
      // Still report canceled; cancel is best-effort
    }
    onClose("canceled");
  };

  const handleOverlayClick = () => {
    if (loading) return;
    handleCancel();
  };

  return (
    <div
      className="transaction-password-overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="transaction-password-title"
    >
      <div
        className="transaction-password-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="transaction-password-title" className="transaction-password-title">
          {dialogTitle}
        </h3>
        {firstMessage && (
          <p className="transaction-password-first-message">{firstMessage}</p>
        )}
        {secondMessage && (
          <p className="transaction-password-second-message">{secondMessage}</p>
        )}
        <label className="transaction-password-label">
          {t("login.password", { defaultValue: "Password" })}
        </label>
        <div className="transaction-password-input-wrapper">
          <CustomInput
            type={showPassword ? "text" : "password"}
            placeholder={t("login.passwordPlaceholder", { defaultValue: "Enter your password" })}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleApply();
              }
            }}
            className="transaction-password-input"
            disabled={loading}
            autoFocus
            autoComplete="current-password"
          />
          <button
            type="button"
            className="transaction-password-toggle"
            onClick={() => setShowPassword((prev) => !prev)}
            disabled={loading}
            aria-label={showPassword ? t("login.hidePassword") : t("login.showPassword")}
          >
            <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
          </button>
        </div>
        {error && <p className="transaction-password-error">{error}</p>}
        <div className="transaction-password-actions">
          <CustomButton
            className="transaction-password-cancel-button"
            variant="secondary"
            onClick={handleCancel}
            disabled={loading}
          >
            <span>Cancel</span>
          </CustomButton>
          <CustomButton
            className="transaction-password-apply-button"
            variant="primary"
            onClick={handleApply}
            disabled={loading || !(password || "").trim()}
          >
            <span>{loading ? "Processing…" : "Apply"}</span>
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
